import GdprSettings from 'tradera/utils/gdpr-settings';
import { STORAGE_KEYS } from 'tradera/constants/storage';
import { logger } from 'packages/logger';
import { isServer } from 'tradera/utils/nextjs';

// KATS: this whole thing would be in better shape if we re-wrote it in TS instead with either inheritance or interfaces.

/**
 * Storage wrapper/utility
 */
export class Storage {
    /**
     * @param {object} storage - Storage-compatible object
     * @param {function} [setFilter] - Functions that returns true/false for a storage key, deciding if it should be set in storage
     */
    constructor(storage, setFilter) {
        this.storage = storage;
        this.setFilter = setFilter;
    }

    get length() {
        return this.storage.length;
    }

    /**
     * @param {int} index
     * @returns {string}
     */
    key(index) {
        return this.storage.key(index);
    }

    /**
     * @param {string} key
     * @returns {null|any}
     */
    getItem(key) {
        const value = this.storage.getItem(key);
        if (value) {
            try {
                return JSON.parse(value);
            } catch (error) {
                logger(error, {
                    contexts: {
                        storage: {
                            action: 'getItem',
                            key,
                            value
                        }
                    }
                });
            }
        }
        return null;
    }

    hasValueForKey(key) {
        const value = this.storage.getItem(key);
        return value !== null;
    }

    enabledByGdprSettings() {
        return gdprSettings.isFunctionalEnabled();
    }

    getItemsByPrefix(prefix) {
        const items = {};
        // eslint-disable-next-line better-mutation/no-mutation
        for (let i = 0; i < this.storage.length; i++) {
            const key = this.key(i);
            if (key.startsWith(prefix)) {
                items[key] = this.getItem(key);
            }
        }
        return items;
    }

    /**
     * @param {string} key
     * @param {any} value
     * @returns {undefined}
     */
    setItem(key, value) {
        if (this.setFilter && !this.setFilter(key)) {
            return undefined;
        }
        try {
            this.storage.setItem(key, JSON.stringify(value));
        } catch (error) {
            logger(error, {
                contexts: {
                    storage: {
                        action: 'setItem',
                        key,
                        stringifiedLength: JSON.stringify(value).length,
                        value
                    }
                }
            });
        }
    }

    /**
     * @param {string} key
     * @returns {undefined}
     */
    removeItem(key) {
        return this.storage.removeItem(key);
    }

    clear() {
        return this.storage.clear();
    }

    /**
     * Removes all items whose keys start with prefix
     * @param {string} prefix
     */
    clearByPrefix(prefix) {
        // Loop in reverse because storage indexes will change as you delete items.
        // eslint-disable-next-line better-mutation/no-mutation
        for (let i = this.storage.length - 1; i >= 0; --i) {
            const key = this.key(i);
            if (key.startsWith(prefix)) {
                this.removeItem(key);
            }
        }
    }
}

/**
 * In-memory Storage object for testing & fallback
 */
export class MemoryStorage {
    constructor() {
        this.storage = new Map();
    }

    get length() {
        return this.storage.size;
    }

    key(index) {
        const keys = Array.from(this.storage.keys());
        if (index in keys) {
            return keys[index];
        } else {
            return null;
        }
    }

    getItem(key) {
        return this.storage.get(key);
    }

    setItem(key, value) {
        this.storage.set(key, value);
    }

    removeItem(key) {
        this.storage.delete(key);
    }

    clear() {
        this.storage.clear();
    }
}

/**
 * Test if a storage implementation is available
 *
 * @param {Storage} storage
 * @returns {boolean}
 */
function testStorage(storage) {
    if (!storage) {
        return false;
    }
    const TEST_KEY = 'STORAGE_TEST';
    const TEST_VALUE = '1';
    try {
        // This will throw an exception in some browsers when cookies/localStorage are explicitly disabled (i.e. Chrome)
        storage.setItem(TEST_KEY, TEST_VALUE);
        if (storage.getItem(TEST_KEY) === TEST_VALUE) {
            storage.removeItem(TEST_KEY);
        } else {
            throw new Error();
        }
    } catch (e) {
        return false;
    }
    return true;
}

let localStorage;
try {
    if (!isServer) {
        // access to window.localStorage is forbidden when user blocks cookies or third party cookies so we safe-guard around this access
        localStorage = window?.localStorage;
    }
} catch (e) {
    localStorage = null;
}

// Standard local store with GDPR cookie filter and in-memory storage fallback
const defaultStorage = testStorage(localStorage)
    ? localStorage
    : new MemoryStorage();
const gdprSettings = GdprSettings;

export const localStore = new Storage(defaultStorage, (key) => {
    if (!Object.values(STORAGE_KEYS).includes(key)) {
        logger(`Storage key '${key}' not found in STORAGE_KEYS`);
        return false;
    }
    if (!gdprSettings.isFunctionalEnabled()) {
        console.warn('Local storage blocked by GDPR settings', true);
        return false;
    }
    return true;
});
